/**
 * Pure reducers for the round state.
 *
 * They are deliberately free of React and of zustand so every protocol edge
 * case (reconnection mid-flight, replayed batch, event for a stale round) is
 * testable as a plain function call.
 *
 * Two rules hold everywhere here:
 *  - an event whose roundId is not the round we are on is DROPPED, never
 *    partially applied: `state:sync` re-establishes the truth anyway;
 *  - the client never settles a bet. A crash does not turn my bets into losses;
 *    only `me:bet` from the server does (the server is the authority).
 *
 * The multiplier is intentionally absent: it changes 60 times per second and
 * lives in MultiplierModel, read straight from the render loops. Putting it here
 * would re-render the whole React tree every frame.
 */
import {
  GAME_DEFAULTS,
  type BalanceEvent,
  type BetsBatchEvent,
  type HistoryEntry,
  type MyBet,
  type OnlineEvent,
  type PublicBet,
  type PublicGameConfig,
  type RoundBettingEvent,
  type RoundCrashEvent,
  type RoundStartEvent,
  type RoundState,
  type StateSync,
} from "@crashx/shared";

import type { RoundReveal } from "@crashx/shared";
export type { RoundReveal } from "@crashx/shared";

export interface RoundStoreState {
  /** Public game config from the server; null until the first `state:sync`. */
  config: PublicGameConfig | null;
  round: RoundState | null;
  /** Newest first, capped at GAME_DEFAULTS.historyLength. */
  history: HistoryEntry[];
  /** Anonymised bets of the current round. */
  bets: PublicBet[];
  /** Bets of the round before, kept for the "Previous" tab. */
  previousBets: PublicBet[];
  myBets: MyBet[];
  balances: Record<string, BalanceEvent>;
  onlinePlayers: number;
  /** Last crash reveal, for the provably-fair panel. */
  lastCrash: RoundCrashEvent | null;
  /** Reveals received in this session, newest first; kept across reconnections. */
  reveals: RoundReveal[];
  lastSyncAt: number | null;
}

export const HISTORY_LIMIT = GAME_DEFAULTS.historyLength;

export const initialRoundState: RoundStoreState = {
  config: null,
  round: null,
  history: [],
  bets: [],
  previousBets: [],
  myBets: [],
  balances: {},
  onlinePlayers: 0,
  lastCrash: null,
  reveals: [],
  lastSyncAt: null,
};

export function applyStateSync(state: RoundStoreState, payload: StateSync): RoundStoreState {
  return {
    ...state,
    config: payload.config,
    round: payload.round,
    history: payload.history.slice(0, HISTORY_LIMIT),
    bets: [...payload.bets],
    previousBets: [],
    myBets: [...payload.myBets],
    onlinePlayers: payload.onlinePlayers,
    lastCrash: null,
    lastSyncAt: payload.serverTime,
  };
}

export function applyRoundBetting(state: RoundStoreState, payload: RoundBettingEvent): RoundStoreState {
  const round: RoundState = {
    roundId: payload.roundId,
    seq: payload.seq,
    phase: "BETTING",
    serverSeedHash: payload.serverSeedHash,
    bettingEndsAt: payload.bettingEndsAt,
    startedAt: null,
    crashAt: null,
  };
  return {
    ...state,
    round,
    previousBets: state.bets,
    bets: [],
    // A "pending" bet was queued for the round that is now opening; anything
    // else belongs to the round that just ended.
    myBets: state.myBets.filter((bet) => bet.status === "pending"),
    lastCrash: null,
  };
}

export function applyRoundStart(state: RoundStoreState, payload: RoundStartEvent): RoundStoreState {
  if (state.round === null || state.round.roundId !== payload.roundId) return state;
  return {
    ...state,
    config:
      state.config && payload.growthK !== undefined
        ? { ...state.config, growthK: payload.growthK }
        : state.config,
    round: { ...state.round, phase: "FLYING", startedAt: payload.startedAt },
  };
}

export function applyRoundCrash(state: RoundStoreState, payload: RoundCrashEvent): RoundStoreState {
  if (state.round === null || state.round.roundId !== payload.roundId) return state;
  const entry: HistoryEntry = {
    roundId: payload.roundId,
    seq: payload.seq,
    crashAt: payload.crashAt,
    crashedAt: payload.crashedAt,
  };
  const reveal: RoundReveal = { ...entry, serverSeedHash: state.round.serverSeedHash, pf: payload.pf };
  return {
    ...state,
    round: { ...state.round, phase: "CRASHED", crashAt: payload.crashAt },
    history: [entry, ...state.history.filter((h) => h.roundId !== payload.roundId)].slice(0, HISTORY_LIMIT),
    reveals: [reveal, ...state.reveals.filter((r) => r.roundId !== payload.roundId)].slice(0, HISTORY_LIMIT),
    lastCrash: payload,
  };
}

export function applyBetsBatch(state: RoundStoreState, payload: BetsBatchEvent): RoundStoreState {
  if (state.round === null || state.round.roundId !== payload.roundId) return state;

  let bets = state.bets;

  if (payload.placed.length > 0) {
    const known = new Set(bets.map((b) => b.betId));
    const added = payload.placed.filter((b) => !known.has(b.betId));
    if (added.length > 0) bets = [...bets, ...added];
  }

  if (payload.cashed.length > 0) {
    const cashedById = new Map(payload.cashed.map((b) => [b.betId, b]));
    const merged = bets.map((b) => cashedById.get(b.betId) ?? b);
    const seen = new Set(bets.map((b) => b.betId));
    // A cashout whose "placed" batch we missed still has to show up.
    const orphans = payload.cashed.filter((b) => !seen.has(b.betId));
    bets = orphans.length > 0 ? [...merged, ...orphans] : merged;
  }

  if (payload.cancelled.length > 0) {
    const dropped = new Set(payload.cancelled);
    bets = bets.filter((b) => !dropped.has(b.betId));
  }

  return bets === state.bets ? state : { ...state, bets };
}

export function applyMyBet(state: RoundStoreState, bet: MyBet): RoundStoreState {
  const index = state.myBets.findIndex((b) => b.betId === bet.betId);
  if (index === -1) return { ...state, myBets: [...state.myBets, bet] };
  const myBets = [...state.myBets];
  myBets[index] = bet;
  return { ...state, myBets };
}

export function applyBalance(state: RoundStoreState, payload: BalanceEvent): RoundStoreState {
  return { ...state, balances: { ...state.balances, [payload.currency]: payload } };
}

export function applyOnline(state: RoundStoreState, payload: OnlineEvent): RoundStoreState {
  return { ...state, onlinePlayers: payload.onlinePlayers };
}
